/** Static mock data for the Labour Management demo screen (Demo Mode only - see the header
 * comment in `index.tsx`). No backend rota/scheduling/training feed exists yet for this
 * screen, so every figure here is a seeded illustrative number, matching the approach used
 * by `src/pages/FinanceDashboard` and `src/mocks/data/*` elsewhere in the app. Sites, roles
 * and service periods are modelled on a multi-site contract catering operator. */

import {
  AbsenceCoverRow,
  CoverageRow,
  DemandAlignmentRow,
  KpiCardData,
  LabourRisk,
  ReadinessIssue,
  ReadinessMetric,
  RotaRow,
  SiteDrilldown,
  WeeklyTrendPoint,
} from './types';

export const SITES = [
  'St Catherine\u2019s School',
  'Greenfield Academy',
  'Riverside Workplace',
  'Kingsway House',
  'Oakfield Care Home',
  'Meridian Events',
] as const;

export const SECTORS = ['Education', 'Workplace', 'Care', 'Hospitality & Events'] as const;

export const SITE_SECTORS: Record<string, typeof SECTORS[number]> = {
  'St Catherine\u2019s School': 'Education',
  'Greenfield Academy': 'Education',
  'Riverside Workplace': 'Workplace',
  'Kingsway House': 'Workplace',
  'Oakfield Care Home': 'Care',
  'Meridian Events': 'Hospitality & Events',
};

export const SERVICE_PERIODS = ['Breakfast', 'Lunch', 'Hospitality', 'Events', 'Dinner'] as const;

export const ROLES = [
  'Chef Manager',
  'Head Chef',
  'Sous Chef',
  'Chef de Partie',
  'Catering Assistant',
  'Front of House',
  'Hospitality Supervisor',
  'Kitchen Porter',
  'Barista',
  'General Assistant',
] as const;

export const KPI_CARDS: KpiCardData[] = [
  { key: 'scheduledHours', label: 'Scheduled hours', value: '612', comparisonLabel: 'vs 605 last week', status: 'good' },
  { key: 'actualHours', label: 'Actual / clocked hours', value: '594', comparisonLabel: '97% of scheduled', status: 'warning' },
  { key: 'labourCostPercent', label: 'Labour cost %', value: '29.8%', comparisonLabel: 'target 28.0%', status: 'warning' },
  { key: 'labourCostVsBudget', label: 'Labour cost vs budget', value: '+3.2%', comparisonLabel: '\u00a31,180 over budget', status: 'critical' },
  { key: 'overtimeHours', label: 'Overtime hours', value: '18.5', comparisonLabel: 'vs 11 last week', status: 'warning' },
  { key: 'absenceRate', label: 'Absence rate', value: '4.6%', comparisonLabel: 'target < 3.5%', status: 'critical' },
  { key: 'unfilledShifts', label: 'Unfilled shifts', value: '3', comparisonLabel: '2 chef, 1 supervisor', status: 'critical' },
  { key: 'sitesAtRisk', label: 'Sites at risk', value: '2 of 6', comparisonLabel: 'Riverside, Meridian Events', status: 'warning' },
  { key: 'trainingCoverage', label: 'Training coverage', value: '91%', comparisonLabel: 'target 95%', status: 'warning' },
];

export const LABOUR_RISKS: LabourRisk[] = [
  {
    id: 'risk-1',
    site: 'Riverside Workplace',
    servicePeriod: 'Lunch',
    severity: 'critical',
    reason: 'Under-covered for lunch by 2 hours \u2013 one Catering Assistant shift unfilled ahead of a 420-cover forecast.',
    action: { label: 'Find cover', target: 'absence' },
  },
  {
    id: 'risk-2',
    site: 'Kingsway House',
    servicePeriod: 'Dinner',
    severity: 'warning',
    reason: 'Overtime risk after 15:00 \u2013 two Kitchen Porters already at 38 hours this week.',
    action: { label: 'Review rota', target: 'rota' },
  },
  {
    id: 'risk-3',
    site: 'Greenfield Academy',
    servicePeriod: 'Lunch',
    severity: 'critical',
    reason: '1 unfilled Head Chef shift for today\u2019s lunch service.',
    action: { label: 'Find cover', target: 'absence' },
  },
  {
    id: 'risk-4',
    site: 'Oakfield Care Home',
    servicePeriod: 'All day',
    severity: 'warning',
    reason: '2 team members scheduled today without required allergen training.',
    action: { label: 'View training', target: 'readiness' },
  },
  {
    id: 'risk-5',
    site: 'Meridian Events',
    servicePeriod: 'Hospitality',
    severity: 'critical',
    reason: 'Tonight\u2019s hospitality event has no assigned Hospitality Supervisor.',
    action: { label: 'Adjust forecast', target: 'cost' },
  },
];

export const COVERAGE_ROWS: CoverageRow[] = [
  { site: 'St Catherine\u2019s School', breakfast: 'Covered', lunch: 'Covered', hospitality: 'Covered', events: 'Covered', dinner: 'Covered', overall: 'Covered' },
  { site: 'Greenfield Academy', breakfast: 'Covered', lunch: 'Understaffed', hospitality: 'Covered', events: 'Covered', dinner: 'Covered', overall: 'Understaffed' },
  { site: 'Riverside Workplace', breakfast: 'Tight', lunch: 'Understaffed', hospitality: 'Covered', events: 'Covered', dinner: 'Tight', overall: 'Understaffed' },
  { site: 'Kingsway House', breakfast: 'Covered', lunch: 'Covered', hospitality: 'Tight', events: 'Covered', dinner: 'Tight', overall: 'Tight' },
  { site: 'Oakfield Care Home', breakfast: 'Covered', lunch: 'Covered', hospitality: 'Covered', events: 'Covered', dinner: 'Overstaffed', overall: 'Covered' },
  { site: 'Meridian Events', breakfast: 'Covered', lunch: 'Covered', hospitality: 'Understaffed', events: 'Understaffed', dinner: 'Covered', overall: 'Understaffed' },
];

export const ROTA_ROWS: RotaRow[] = [
  { id: 'rota-1', site: 'St Catherine\u2019s School', servicePeriod: 'Lunch', role: 'Chef Manager', scheduled: 8, actual: 8, labourCost: 152, status: 'good', note: '', resolved: true },
  { id: 'rota-2', site: 'St Catherine\u2019s School', servicePeriod: 'Lunch', role: 'Catering Assistant', scheduled: 24, actual: 23.5, labourCost: 258, status: 'good', note: '', resolved: true },
  { id: 'rota-3', site: 'Greenfield Academy', servicePeriod: 'Lunch', role: 'Head Chef', scheduled: 8, actual: 0, labourCost: 0, status: 'critical', note: 'Shift unfilled \u2013 sickness, no cover assigned yet', resolved: false },
  { id: 'rota-4', site: 'Greenfield Academy', servicePeriod: 'Breakfast', role: 'General Assistant', scheduled: 6, actual: 6.2, labourCost: 62, status: 'good', note: '', resolved: true },
  { id: 'rota-5', site: 'Riverside Workplace', servicePeriod: 'Lunch', role: 'Catering Assistant', scheduled: 16, actual: 8, labourCost: 84, status: 'critical', note: 'Unfilled shift \u2013 under-covered by 2 hours for 420-cover forecast', resolved: false },
  { id: 'rota-6', site: 'Riverside Workplace', servicePeriod: 'Breakfast', role: 'Barista', scheduled: 12, actual: 11.5, labourCost: 98, status: 'warning', note: 'Late clock-in, 30 min short', resolved: false },
  { id: 'rota-7', site: 'Kingsway House', servicePeriod: 'Dinner', role: 'Kitchen Porter', scheduled: 16, actual: 18.5, labourCost: 176, status: 'warning', note: 'Trending into overtime after 15:00', resolved: false },
  { id: 'rota-8', site: 'Kingsway House', servicePeriod: 'Hospitality', role: 'Hospitality Supervisor', scheduled: 8, actual: 8, labourCost: 168, status: 'good', note: '', resolved: true },
  { id: 'rota-9', site: 'Oakfield Care Home', servicePeriod: 'Dinner', role: 'Sous Chef', scheduled: 8, actual: 8, labourCost: 144, status: 'good', note: '', resolved: true },
  { id: 'rota-10', site: 'Oakfield Care Home', servicePeriod: 'Lunch', role: 'General Assistant', scheduled: 8, actual: 12, labourCost: 108, status: 'warning', note: 'Overstaffed \u2013 extra cover kept on from morning deep clean', resolved: false },
  { id: 'rota-11', site: 'Meridian Events', servicePeriod: 'Hospitality', role: 'Hospitality Supervisor', scheduled: 6, actual: 0, labourCost: 0, status: 'critical', note: 'No supervisor assigned to tonight\u2019s event', resolved: false },
  { id: 'rota-12', site: 'Meridian Events', servicePeriod: 'Events', role: 'Front of House', scheduled: 20, actual: 16, labourCost: 176, status: 'warning', note: '2 team members yet to clock in', resolved: false },
];

export const WEEKLY_TREND: WeeklyTrendPoint[] = [
  { day: 'Mon', scheduledHours: 588, actualHours: 580, labourCostPercentage: 28.1, absenceRate: 2.8, overtimeHours: 6 },
  { day: 'Tue', scheduledHours: 594, actualHours: 588, labourCostPercentage: 28.4, absenceRate: 3.1, overtimeHours: 7.5 },
  { day: 'Wed', scheduledHours: 602, actualHours: 597, labourCostPercentage: 28.9, absenceRate: 3.4, overtimeHours: 9 },
  { day: 'Thu', scheduledHours: 605, actualHours: 599, labourCostPercentage: 29.2, absenceRate: 3.9, overtimeHours: 11 },
  { day: 'Fri', scheduledHours: 610, actualHours: 601, labourCostPercentage: 29.5, absenceRate: 4.1, overtimeHours: 14 },
  { day: 'Sat', scheduledHours: 615, actualHours: 592, labourCostPercentage: 30.1, absenceRate: 4.4, overtimeHours: 16.5 },
  { day: 'Today', scheduledHours: 612, actualHours: 594, labourCostPercentage: 29.8, absenceRate: 4.6, overtimeHours: 18.5 },
];

export const DEMAND_ALIGNMENT: DemandAlignmentRow[] = [
  { id: 'demand-1', site: 'St Catherine\u2019s School', servicePeriod: 'Lunch', forecastLabel: '610 covers', requiredHours: 32, scheduledHours: 32, variance: 0, labourCostPerCover: 1.05, status: 'good' },
  { id: 'demand-2', site: 'Greenfield Academy', servicePeriod: 'Lunch', forecastLabel: '540 covers', requiredHours: 30, scheduledHours: 26, variance: -4, labourCostPerCover: 1.24, status: 'warning' },
  { id: 'demand-3', site: 'Riverside Workplace', servicePeriod: 'Lunch', forecastLabel: '420 covers', requiredHours: 38, scheduledHours: 36, variance: -2, labourCostPerCover: 1.18, status: 'warning' },
  { id: 'demand-4', site: 'Kingsway House', servicePeriod: 'Dinner', forecastLabel: '310 covers', requiredHours: 26, scheduledHours: 27, variance: 1, labourCostPerCover: 1.02, status: 'good' },
  { id: 'demand-5', site: 'Oakfield Care Home', servicePeriod: 'Dinner', forecastLabel: '180 covers', requiredHours: 20, scheduledHours: 22, variance: 2, labourCostPerCover: 1.45, status: 'warning' },
  { id: 'demand-6', site: 'Meridian Events', servicePeriod: 'Hospitality', forecastLabel: '120 guests', requiredHours: 14, scheduledHours: 10, variance: -4, labourCostPerCover: 1.78, status: 'critical' },
];

export const ABSENCE_COVER: AbsenceCoverRow[] = [
  { id: 'absence-1', employee: 'J. Adeyemi', role: 'Head Chef', site: 'Greenfield Academy', shift: 'Lunch, 10:00\u201314:00', reason: 'Sickness', coverStatus: 'Uncovered', impact: 'Lunch service at risk \u2013 no chef cover for 540-cover forecast' },
  { id: 'absence-2', employee: 'M. Kowalski', role: 'Catering Assistant', site: 'Riverside Workplace', shift: 'Lunch, 11:00\u201315:00', reason: 'Sickness', coverStatus: 'Seeking cover', impact: 'Lunch under-covered by 2 hours' },
  { id: 'absence-3', employee: 'R. Osei', role: 'Hospitality Supervisor', site: 'Meridian Events', shift: 'Hospitality, 17:00\u201323:00', reason: 'Annual leave (unplanned swap)', coverStatus: 'Uncovered', impact: 'No supervisor assigned to tonight\u2019s hospitality event' },
  { id: 'absence-4', employee: 'S. Patel', role: 'Kitchen Porter', site: 'Kingsway House', shift: 'Dinner, 15:00\u201321:00', reason: 'Sickness', coverStatus: 'Covered', impact: 'Covered by overtime \u2013 contributing to overtime risk after 15:00' },
  { id: 'absence-5', employee: 'A. Nowak', role: 'Barista', site: 'Riverside Workplace', shift: 'Breakfast, 06:00\u201310:00', reason: 'Late \u2013 travel disruption', coverStatus: 'Covered', impact: 'Minor \u2013 30 minute short clock-in, no service impact' },
];

export const READINESS_METRICS: ReadinessMetric[] = [
  { key: 'scheduledTeamMembers', label: 'Scheduled team members (today)', value: '86', status: 'good' },
  { key: 'foodSafetyCoverage', label: 'Food safety training coverage', value: '94%', status: 'good' },
  { key: 'allergenCoverage', label: 'Allergen training coverage', value: '88%', status: 'warning' },
  { key: 'expiredCertificates', label: 'Scheduled team members with expired training', value: '3', status: 'critical' },
  { key: 'supervisorReadiness', label: 'Event supervisors missing hospitality briefing', value: '1', status: 'critical' },
];

export const READINESS_ISSUES: ReadinessIssue[] = [
  { id: 'readiness-1', site: 'Oakfield Care Home', description: '2 Catering Assistants scheduled today without required allergen training.', severity: 'warning' },
  { id: 'readiness-2', site: 'Meridian Events', description: 'Hospitality Supervisor for tonight\u2019s event has not completed the required hospitality briefing.', severity: 'critical' },
  { id: 'readiness-3', site: 'Riverside Workplace', description: '1 Barista\u2019s food safety certificate expired last week and has not been renewed.', severity: 'critical' },
  { id: 'readiness-4', site: 'Kingsway House', description: '2 Kitchen Porters\u2019 food safety certificates expire within 7 days.', severity: 'warning' },
];

export const SITE_DRILLDOWNS: Record<string, SiteDrilldown> = {
  'St Catherine\u2019s School': {
    site: 'St Catherine\u2019s School',
    sector: 'Education',
    servicePeriods: ['Breakfast', 'Lunch'],
    scheduledRoles: [
      { role: 'Chef Manager', scheduled: 1, clockedIn: 1 },
      { role: 'Catering Assistant', scheduled: 4, clockedIn: 4 },
      { role: 'General Assistant', scheduled: 2, clockedIn: 2 },
    ],
    openShifts: 0,
    overtimeWarning: null,
    absenceNotes: [],
    trainingIssues: [],
    labourCost: 410,
    labourBudget: 420,
    suggestedActions: ['No action needed \u2013 fully covered'],
  },
  'Greenfield Academy': {
    site: 'Greenfield Academy',
    sector: 'Education',
    servicePeriods: ['Breakfast', 'Lunch'],
    scheduledRoles: [
      { role: 'Head Chef', scheduled: 1, clockedIn: 0 },
      { role: 'Catering Assistant', scheduled: 3, clockedIn: 3 },
      { role: 'General Assistant', scheduled: 2, clockedIn: 2 },
    ],
    openShifts: 1,
    overtimeWarning: null,
    absenceNotes: ['J. Adeyemi (Head Chef) \u2013 sickness, uncovered'],
    trainingIssues: [],
    labourCost: 386,
    labourBudget: 402,
    suggestedActions: ['Find cover for Head Chef before 11:30 lunch service', 'Escalate to Regional Manager if uncovered by 11:00'],
  },
  'Riverside Workplace': {
    site: 'Riverside Workplace',
    sector: 'Workplace',
    servicePeriods: ['Breakfast', 'Lunch'],
    scheduledRoles: [
      { role: 'Chef Manager', scheduled: 1, clockedIn: 1 },
      { role: 'Catering Assistant', scheduled: 2, clockedIn: 1 },
      { role: 'Barista', scheduled: 2, clockedIn: 2 },
    ],
    openShifts: 1,
    overtimeWarning: null,
    absenceNotes: ['M. Kowalski (Catering Assistant) \u2013 sickness, seeking cover'],
    trainingIssues: [],
    labourCost: 440,
    labourBudget: 410,
    suggestedActions: ['Find cover for lunch \u2013 under-covered by 2 hours against 420-cover forecast', 'Consider redeploying from Kingsway House for the lunch window'],
  },
  'Kingsway House': {
    site: 'Kingsway House',
    sector: 'Workplace',
    servicePeriods: ['Lunch', 'Hospitality', 'Dinner'],
    scheduledRoles: [
      { role: 'Sous Chef', scheduled: 1, clockedIn: 1 },
      { role: 'Kitchen Porter', scheduled: 2, clockedIn: 2 },
      { role: 'Hospitality Supervisor', scheduled: 1, clockedIn: 1 },
    ],
    openShifts: 0,
    overtimeWarning: '2 Kitchen Porters projected to exceed 40 hours this week if dinner overtime continues',
    absenceNotes: [],
    trainingIssues: ['2 Kitchen Porters\u2019 food safety certificates expire within 7 days'],
    labourCost: 488,
    labourBudget: 460,
    suggestedActions: ['Review dinner rota to cap overtime after 15:00', 'Book food safety refresher for Kitchen Porters before certificates expire'],
  },
  'Oakfield Care Home': {
    site: 'Oakfield Care Home',
    sector: 'Care',
    servicePeriods: ['Lunch', 'Dinner'],
    scheduledRoles: [
      { role: 'Sous Chef', scheduled: 1, clockedIn: 1 },
      { role: 'General Assistant', scheduled: 3, clockedIn: 3 },
    ],
    openShifts: 0,
    overtimeWarning: null,
    absenceNotes: [],
    trainingIssues: ['2 Catering Assistants scheduled without required allergen training'],
    labourCost: 396,
    labourBudget: 380,
    suggestedActions: ['Book allergen training refresher for scheduled Catering Assistants', 'Review General Assistant overstaffing on lunch shift'],
  },
  'Meridian Events': {
    site: 'Meridian Events',
    sector: 'Hospitality & Events',
    servicePeriods: ['Hospitality', 'Events'],
    scheduledRoles: [
      { role: 'Hospitality Supervisor', scheduled: 1, clockedIn: 0 },
      { role: 'Front of House', scheduled: 5, clockedIn: 4 },
    ],
    openShifts: 1,
    overtimeWarning: null,
    absenceNotes: ['R. Osei (Hospitality Supervisor) \u2013 unplanned leave swap, uncovered'],
    trainingIssues: ['Assigned supervisor has not completed the required hospitality briefing'],
    labourCost: 352,
    labourBudget: 330,
    suggestedActions: ['Assign a supervisor before tonight\u2019s event start at 17:00', 'Confirm hospitality briefing completion for any stand-in supervisor'],
  },
};
